import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validatePassword, validateDisplayName } from '@hygh-vybe/lib';
import { COGNITO_DOMAIN, COGNITO_CLIENT_ID } from '../../config';
import './auth.css';

interface FieldErrors {
  email?: string;
  password?: string;
  displayName?: string;
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const errors: FieldErrors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = 'Enter a valid email address';
    }

    const passwordResult = validatePassword(password);
    if (!passwordResult.success) {
      errors.password = passwordResult.errors[0]?.reason || 'Invalid password';
    }

    const nameResult = validateDisplayName(displayName);
    if (!nameResult.success) {
      errors.displayName = nameResult.errors[0]?.reason || 'Invalid display name';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validate()) return;

    setSubmitting(true);
    try {
      await register(email.trim(), password, displayName.trim());
      sessionStorage.setItem('ws_just_signed_up', 'true');
      navigate('/auth/verify', { state: { email: email.trim() } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialLogin = (provider: 'google' | 'apple') => {
    // Save provider and return path so the callback page knows where to go
    localStorage.setItem('ws_oauth_provider', provider);
    localStorage.setItem('ws_oauth_return_to', '/');

    // Build Cognito hosted UI authorize URL
    const cognitoDomain = COGNITO_DOMAIN;
    const clientId = COGNITO_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/callback`;
    const identityProvider = provider === 'google' ? 'Google' : 'SignInWithApple';

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: clientId,
      redirect_uri: redirectUri,
      identity_provider: identityProvider,
      scope: 'openid email profile',
    });

    window.location.href = `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`;
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Create account</h1>
        <p className="auth-subtitle">Join to rate and review THC beverages</p>

        <div className="social-buttons">
          <button
            type="button"
            className="social-btn social-btn--google"
            onClick={() => handleSocialLogin('google')}
            aria-label="Continue with Google"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            Continue with Google
          </button>

          {/* TODO: Enable Apple sign-in when credentials are configured
          <button
            type="button"
            className="social-btn social-btn--apple"
            onClick={() => handleSocialLogin('apple')}
            aria-label="Continue with Apple"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
            Continue with Apple
          </button>
          */}
        </div>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {error && <div className="auth-error" role="alert">{error}</div>}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label htmlFor="register-name">Display name</label>
            <input
              id="register-name"
              type="text"
              autoComplete="name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              className={fieldErrors.displayName ? 'field-error' : ''}
              required
            />
            {fieldErrors.displayName && (
              <span className="field-error-msg">{fieldErrors.displayName}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="register-email">Email</label>
            <input
              id="register-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={fieldErrors.email ? 'field-error' : ''}
              required
            />
            {fieldErrors.email && (
              <span className="field-error-msg">{fieldErrors.email}</span>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="register-password">Password</label>
            <input
              id="register-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="8+ chars, uppercase, lowercase, number"
              className={fieldErrors.password ? 'field-error' : ''}
              required
            />
            {fieldErrors.password && (
              <span className="field-error-msg">{fieldErrors.password}</span>
            )}
          </div>

          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account? <Link to="/auth/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
